import { useNavigate } from "react-router-dom";
import { FaChartLine, FaShieldAlt, FaGlobe, FaDollarSign, FaUsers, FaBuilding } from "react-icons/fa";
import "./GettingStartedShared.css";

const General3 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
        {
            title: "How the USA Incorporation Process Works",
            description: "A step-by-step guide to incorporating your business in the United States.",
            link: "/how-the-usa-incorporation-process-word"
        },
        {
            title: "Can a Non-US Resident or Non-US Citizen Incorporate in the US?",
            description: "Learn about the requirements and process for non-residents to incorporate in the US.",
            link: "/can-a-non-us-resident-or-non-us-citizen-incorporate-in-the-us"
        },
        {
            title: "What Are the Ongoing Compliance Requirements After US Incorporation?",
            description: "Understand the legal and tax obligations for maintaining your US business entity.",
            link: "/what-are-the-ongoing-compliance-requirements-after-us-incorporation?"
        }
    ];

    const benefits = [
        {
            icon: <FaChartLine className="gs-benefit-icon" />,
            title: "Access to Global Investors & Venture Capital",
            description: "U.S. startups are more attractive to investors, especially venture capitalists (VCs) and angel investors. Many Silicon Valley investors and funding programs require companies to be incorporated in the U.S., especially as a Delaware C-Corporation.",
            points: [
                "Attract international investors",
                "Access to Silicon Valley funding networks",
                "Preferred structure for venture capital"
            ]
        },
        {
            icon: <FaShieldAlt className="gs-benefit-icon" />,
            title: "Strong Legal & Business Protections",
            description: "The U.S. has strong corporate laws that provide clarity, fairness, and protection for businesses.",
            points: [
                "Limited liability protection",
                "Well-established legal framework",
                "Intellectual property protection"
            ]
        },
        {
            icon: <FaGlobe className="gs-benefit-icon" />,
            title: "Global Market Access & Credibility",
            description: "A U.S. company is often seen as more credible by customers, partners, and investors worldwide.",
            points: [
                "Enhanced brand perception and trust",
                "Easier to open U.S. bank accounts and process payments",
                "Better access to U.S. and international clients"
            ]
        },
        {
            icon: <FaDollarSign className="gs-benefit-icon" />,
            title: "Favorable Tax Benefits & Incentives",
            description: "Optimize your tax strategy with U.S. incorporation benefits.",
            points: [
                "Tax advantages in states like Wyoming and Delaware",
                "Flexibility in business structure (LLC vs. C-Corp)",
                "Access to beneficial tax treaties",
                "Potential tax deductions and credits"
            ]
        },
        {
            icon: <FaUsers className="gs-benefit-icon" />,
            title: "Easier Hiring of Global Talent",
            description: "Build your dream team with access to global talent.",
            points: [
                "Hire employees and contractors worldwide",
                "Sponsor work visas (H-1B, L-1, etc.) for international talent",
                "Access to a diverse and skilled workforce",
                "Competitive advantage in talent acquisition"
            ]
        },
        {
            icon: <FaBuilding className="gs-benefit-icon" />,
            title: "Business-Friendly Banking & Financial Services",
            description: "Access world-class financial services and banking solutions.",
            points: [
                "U.S. bank accounts and merchant services",
                "Integration with global payment processors",
                "Business loans and credit facilities",
                "Multi-currency support"
            ]
        }
    ];

    return (
        <div className="gs-container">
            <header className="gs-header">
                <div className="gs-container-1200">
                </div>
            </header>
            
            <main className="gs-main-content">
                <div className="gs-container-1200">
                    <nav className="gs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/getting-started">Getting Started</a></li>
                            <li>General</li>
                        </ol>
                    </nav>

                    <article className="gs-article">
                        <header className="gs-article-header">
                            <h1 className="gs-article-title">Why Should Your Startup Incorporate in the USA?</h1>
                            <div className="gs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="gs-article-content">
                            <p className="gs-lead">
                                The United States is one of the best places to start and grow a business. Whether you are a U.S. resident or an international entrepreneur, incorporating your startup in the U.S. offers significant benefits. Here's why:
                            </p>

                            {benefits.map((benefit, index) => (
                                <div key={index} className="gs-benefit-card">
                                    <div className="gs-benefit-header">
                                        <div className="gs-benefit-icon-container">
                                            {benefit.icon}
                                        </div>
                                        <h2>{index + 1}. {benefit.title}</h2>
                                    </div>
                                    <p>{benefit.description}</p>
                                    {benefit.points && (
                                        <ul className="gs-tips-list">
                                            {benefit.points.map((point, i) => (
                                                <li key={i}>{point}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                            {/* <div className="gs-cta-box">
                                <h3>Ready to Incorporate Your Startup in the US?</h3>
                                <p>Whether you're based in the U.S. or abroad, we make the incorporation process simple and hassle-free. Get started today and take advantage of all these benefits!</p>
                                <div className="gs-cta-buttons">
                                    <button 
                                        className="gs-cta-btn"
                                        onClick={() => navigate('/contact')}
                                    >
                                        Speak with an Expert
                                    </button>
                                    <button 
                                        className="gs-cta-btn secondary"
                                        onClick={() => navigate('/pricing')}
                                    >
                                        View Pricing Plans
                                    </button>
                                </div>
                            </div> */}
                        </div>
                    </article>
                    
                    {/* <div className="gs-helpful-section">
                        <h3 className="gs-helpful-title">Was this article helpful?</h3>
                        <div className="gs-helpful-buttons">
                            <button className="gs-helpful-btn positive">
                                <FaCheckCircle /> Yes, it was helpful
                            </button>
                            <button className="gs-helpful-btn negative">
                                <FaTimesCircle /> No, I need more help
                            </button>
                        </div>
                    </div> */}
                    
                    <div className="gs-related-articles">
                        <h2 className="gs-related-title">Related Articles</h2>
                        <div className="gs-related-grid">
                            {relatedArticles.map((article, index) => (
                                <a 
                                    key={index} 
                                    href={article.link} 
                                    className="gs-related-card"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(article.link);
                                    }}
                                >
                                    <h3>{article.title}</h3>
                                    <p>{article.description}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default General3;